import { useState } from 'react'
import { Org } from '@integration-app/sdk'
import { TbClockCheck, TbInfoCircle } from 'react-icons/tb'

import { Alert } from 'components/Alert'
import useOrg from 'components/Orgs/org-context'
import { Popup } from 'components/Popup'
import useApi from 'hooks/useApi'
import useLocalStorage from 'hooks/useLocalStorage'
import { getTrialDays } from 'routes/components/Layout/MainMenuTrialIndicator'
import { Flex } from 'styled-system/jsx'
import { Button } from 'ui-kit/button'
import { Heading } from 'ui-kit/heading'
import { Text } from 'ui-kit/text'
import { withErrorToast } from 'utils/withErrorToast'

const DAY_IN_MS = 24 * 60 * 60 * 1000

export function ManageTrial({ onClose }: { onClose?: () => void }) {
  const { org } = useOrg()
  const { apiClient, unsetImpersonationToken, getImpersonationToken } = useApi()

  const [requestedSubscriptionDate, setRequestedSubscriptionDate] =
    useLocalStorage('requestedSubscriptionDate', '')

  const [isTrialExtendable, setIsTrialExtendable] = useState(true)
  const [isRequestingTrialExtension, setIsRequestingTrialExtension] =
    useState(false)
  const [wasTrialExtended, setWasTrialExtended] = useState(false)

  const daysOfTrial = org.trialEndDate
    ? getTrialDays(new Date(org.trialEndDate))
    : 0

  const canRequestSubscription =
    !requestedSubscriptionDate ||
    new Date().getTime() - new Date(requestedSubscriptionDate).getTime() >
      DAY_IN_MS * 7

  const requestSubscription = async () => {
    setRequestedSubscriptionDate(new Date().toISOString())
    await apiClient.post('/request-subscription')
  }

  const requestTrialExtension = async () => {
    const canExtend = canAutoExtendTrial(org)

    await withErrorToast(async () => {
      setIsRequestingTrialExtension(true)
      await apiClient.post('/request-trial-extension')
    })

    if (canExtend) {
      setWasTrialExtended(true)
    } else {
      setIsTrialExtendable(false)
    }

    setIsRequestingTrialExtension(false)
  }

  const dropImpersonation = () => {
    unsetImpersonationToken()
    window.location.pathname = '/'
  }
  const isImpersonating = !!getImpersonationToken()

  return (
    <Popup.Root open onOpenChange={onClose}>
      <Popup.Window usePortal width='xl' height='xs' hideCloseButton={!onClose}>
        <Popup.Content
          display='flex'
          flexDirection='column'
          alignItems='center'
          justifyContent='center'
          gap='2'
        >
          <Heading m={0} textStyle={'xl'} textAlign={'center'} as={'h1'}>
            {daysOfTrial > 0
              ? `Your free trial ends in ${daysOfTrial} days`
              : 'Your free trial has expired'}
          </Heading>
          <Text m={0} textAlign={'center'} textWrap='pretty'>
            {daysOfTrial > 0 ? (
              <>
                If you need more time to evaluate integration.app, please
                request the trial extension.
              </>
            ) : (
              <>
                You can continue using the product after you subscribe.
                <br /> If you need more time to evaluate integration.app, please
                request the trial extension.
              </>
            )}
          </Text>
          <Flex
            marginBlockStart={4}
            gap={4}
            justifyContent={'center'}
            alignItems={'center'}
            flexDirection='column'
            width='100%'
          >
            {!isImpersonating && (
              <>
                <Flex gap={4} justifyContent={'center'} alignItems={'center'}>
                  {canRequestSubscription && (
                    <Button
                      onClick={requestSubscription}
                      variant='solid'
                      disabled={!canRequestSubscription}
                    >
                      {canRequestSubscription
                        ? 'Request Subscription'
                        : 'Subscription Requested. Someone will be in touch with you soon.'}
                    </Button>
                  )}
                  {canRequestSubscription && (
                    <>
                      <span>or</span>

                      <Button
                        onClick={requestTrialExtension}
                        variant='outline'
                        loading={isRequestingTrialExtension}
                        disabled={
                          !isTrialExtendable ||
                          isRequestingTrialExtension ||
                          wasTrialExtended
                        }
                      >
                        Request Trial Extension
                      </Button>
                    </>
                  )}
                </Flex>

                {!isTrialExtendable && (
                  <Alert.Root
                    variant='info'
                    icon={<TbInfoCircle />}
                    width='100%'
                    marginTop={2}
                  >
                    <Alert.Description>
                      Trial extensions are available only once every 30 days.
                      Someone will be in touch with you shortly about the
                      additional trial extension.
                    </Alert.Description>
                  </Alert.Root>
                )}

                {wasTrialExtended && (
                  <Alert.Root
                    variant='info'
                    icon={<TbClockCheck />}
                    width='100%'
                    marginTop={2}
                  >
                    <Alert.Description>
                      Your trial was extended for 7 more days. If you need it to
                      last longer, please contact us
                    </Alert.Description>
                  </Alert.Root>
                )}
              </>
            )}
            {isImpersonating && (
              <Button onClick={dropImpersonation}>Delete impersonation</Button>
            )}
          </Flex>
        </Popup.Content>
      </Popup.Window>
    </Popup.Root>
  )
}

function canAutoExtendTrial(org: Org) {
  // no prior extension
  if (!org.lastTrialExtensionDate) {
    return true
  }

  const currentDate = new Date()
  const lastExtensionDate = new Date(org.lastTrialExtensionDate)

  // more than 30 days since last extension
  if (currentDate.getTime() - lastExtensionDate.getTime() > 30 * DAY_IN_MS) {
    return true
  }

  return false
}
